import type { EntityState } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { createComparer } from '../../../utils/object/object';
import { getItems, getItemsCategoryRecommendations } from '../../actions/api/items';
import { setMarket } from '../intl';
import { mergeStateReducerBuilder } from '../../reducers/generic/mergeState';
import type { ItemDTO } from '../../mappers/dtoMappers';
import type { z } from 'zod';
import type { ItemCategoryRecommendationsSchema } from '../../../schemas/items';

type ItemCategoryRecommendations = z.infer<typeof ItemCategoryRecommendationsSchema>;

interface ItemState extends EntityState<ItemDTO, string> {
  categoryRecommendations: ItemCategoryRecommendations | null;
  categoryRecommendationsLoading: boolean;
  categoryRecommendationsLoaded: boolean;
}

const itemEntityAdapter = createEntityAdapter<ItemDTO, string>({
  selectId: (item) => item.itemNo,
  sortComparer: createComparer('itemNameOfficial'),
});

const initialState: ItemState = {
  ...itemEntityAdapter.getInitialState(),
  categoryRecommendations: null,
  categoryRecommendationsLoading: false,
  categoryRecommendationsLoaded: false,
};

const itemSlice = createSlice({
  name: 'item',
  initialState,
  extraReducers: (builder) =>
    mergeStateReducerBuilder('state.item', builder)
      .addCase(setMarket, () => initialState)
      .addCase(getItems.fulfilled, (state, action) => {
        const {
          items,
          meta: { totalNumberOfElements },
        } = action.payload;
        if (typeof totalNumberOfElements === 'number') {
          itemEntityAdapter.setMany(state, items);
        }
      })
      .addCase(getItemsCategoryRecommendations.pending, (state) => {
        state.categoryRecommendationsLoading = true;
      })
      .addCase(getItemsCategoryRecommendations.fulfilled, (state, action) => {
        state.categoryRecommendations = action.payload;
        state.categoryRecommendationsLoading = false;
        state.categoryRecommendationsLoaded = true;
      })
      .addCase(getItemsCategoryRecommendations.rejected, (state) => {
        state.categoryRecommendationsLoading = false;
      }),
  reducers: {},
});

export default itemSlice.reducer;

export const { selectAll: selectItems, selectById: selectItem } =
  itemEntityAdapter.getSelectors(
    (state: { item: EntityState<ItemDTO, string> }) => state.item,
  );

export const selectCategoryRecommendations = (state: {
  item: ItemState;
}): ItemCategoryRecommendations | null => state.item.categoryRecommendations;

export const selectCategoryRecommendationsLoading = (state: {
  item: ItemState;
}): boolean => state.item.categoryRecommendationsLoading;

export const selectCategoryRecommendationsLoaded = (state: {
  item: ItemState;
}): boolean => state.item.categoryRecommendationsLoaded;
